import { Divider } from "@mui/material";
import csi from "csi/main";
import { ControllerProps } from "csi/middleware/common";
import DojoView from "csi/page/DojoView";
import SaveDialog from "designSystem/components/dialog/SaveDialog";
import { useLocalReactionStore } from "designSystem/hooks";
import { FlexColumn, FlexRow, GridColumn, GridRow, ListItem, Message, OverflowText } from "designSystem/index";
import { useLocale } from "designSystem/locale";
import { black, green, grey } from "designSystem/styles/color";
import { observer } from "mobx-react-lite";
import React, { FunctionComponent } from "react";
import { ExpandableList } from "src/Sourcing/components/ExpandableList";
import Text from "../../../designSystem/components/text/Text";
import { createNewAllocateSupplierRequestStore } from "./NewAllocatedSupplierRequestService";
import { NewAllocatedSupplierRequestStoreContext, useNewAllocatedSupplierRequestStore } from "./NewAllocatedSupplierRequestStore";

export const NewAllocatedSupplierRequestsDialog: FunctionComponent<ControllerProps> = observer((props) => {
	const { actionController, onClose } = props;
	const loc = useLocale();
	const store = useLocalReactionStore(() => createNewAllocateSupplierRequestStore(actionController, loc));
	const { saveDialogProps } = store || {};

	return (
		<>
			{store && (
				<NewAllocatedSupplierRequestStoreContext.Provider value={store}>
					<SaveDialog
						{...saveDialogProps}
						disableEnforceFocus
						fullHeight
						onClose={onClose}
						open
						rightDrawerProps={{
							disabled: true,
							drawerNode: (
								<NewAllocateSupplierRequestDrawer />
							),
							open: true,
							widthOpen: 300,
						}}
					>
						{{
							main: (
								<NewAllocatedSupplierRequestForm />
							),
						}}
					</SaveDialog>
				</NewAllocatedSupplierRequestStoreContext.Provider>
			)}
		</>
	);
});

export const NewAllocatedSupplierRequestForm: FunctionComponent = observer(() => {
	const store = useNewAllocatedSupplierRequestStore();
	const { formId, formInfoMsg, nodeType, viewParams, onViewLoad } = store || {};

	return (
		<GridColumn
			spacing={3}
			width={528}
		>
			<Message
				titleText={formInfoMsg}
				variant="info"
			/>
			<DojoView
				fullHeight
				nodeUrl={null}
				onViewLoad={onViewLoad}
				viewId={formId}
				viewNodeType={nodeType}
				viewParams={viewParams}
			/>
		</GridColumn>
	);
});

export const NewAllocateSupplierRequestDrawer: FunctionComponent = () => {
	return (
		<FlexColumn
			bgcolor={grey[200]}
			gap={2}
			p={2}
		>
			<Text res="SupplierRequestOverview" variant="h5" />
			<SourcesSelectedSection />
			<StatisticSection />
		</FlexColumn>
	);
};

const SourcesSelectedSection: FunctionComponent = observer(() => {
	const store = useNewAllocatedSupplierRequestStore();
	const { sourceType, sourcePluralLabel, selectedSources, unallocatedProductsListTitle } = store || {};
	const loc = useLocale();

	const listItems = selectedSources.filter((source) => source.children?.length > 0 || source.id !== csi.EMPTY_URL)
		.map((source) => {
			const items = source.children?.map((item) => {
				return (
					<ListItem key={item.id}>
						<FlexRow
							flexGrow={1}
							gap={2}
							justifyContent="space-between"
						>
							<OverflowText key={item.id} text={loc.getNameOrUnnamed(item.name)} />
							{source.isExisting && (
								<Text
									color={item.isExisting ? "" : green[600]}
									fontStyle="italic"
									res={item.isExisting ? "Existing" : "New"}
								/>
							)}
						</FlexRow>
					</ListItem>
				);
			});
			let customStyle = {};
			if (!source.name || source.isExisting) {
				customStyle = {
					sx: {
						bgcolor: grey[50],
						border: `1px ${source.isExisting ? "solid" : "dotted"} ${grey[400]}`,
						borderRadius: 1,
					},
					titleColor: `${source.isExisting ? black[400] : grey[800]}`,
					labelStatusRes: `${source.isExisting ? "Existing" : ""}`,
				};
			}
			return (
				<ExpandableList
					displayCounts
					items={items}
					key={source.id}
					labelStatusColor={source.isExisting ? "" : green[600]}
					labelStatusRes="New"
					title={source.name || unallocatedProductsListTitle}
					{...customStyle}
				/>
			);
		},
		);

	return (
		<>
			{sourceType && (
				<Text
					color={grey[800]}
					text={sourcePluralLabel}
					variant="h5"
				/>
			)}
			<FlexColumn
				flexGrow={0}
				gap={2}
				overflow="auto scroll"
			>
				{selectedSources.length === 0 && <Text res="DialogSelectionEmpty" />}
				{listItems}
			</FlexColumn>
		</>
	);
});

const StatisticSection: FunctionComponent = observer(() => {
	const store = useNewAllocatedSupplierRequestStore();
	const { masterToBeCreated, productsToBeAddedInMaster, productsAddedInMasterRes, srToBeCreated } = store || {};

	return (
		<GridColumn spacing={2}>
			<Divider />
			<GridRow
				alignItems="center"
				growChild={0}
			>
				<Text res="SupplierRequestMasterGenerated" />
				<Text text={masterToBeCreated} />
			</GridRow>
			<GridRow
				alignItems="center"
				growChild={0}
			>
				<Text res={productsAddedInMasterRes} />
				<Text text={productsToBeAddedInMaster} />
			</GridRow>
			<GridRow
				alignItems="center"
				growChild={0}
			>
				<Text res="SupplierRequestsGenerated" />
				<Text text={srToBeCreated} />
			</GridRow>
		</GridColumn>
	);
});

export default NewAllocatedSupplierRequestsDialog;
